import { useEffect, useState } from 'react';
import { PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import ConfirmModal from 'shared_items/ConfirmModal';
import DatePicker from 'shared_items/DatePicker';
import { formatDate } from 'shared_items/formatConventions';
import {
  createCoreCycle,
  updateCoreCycle,
  deleteCoreCycle,
  uploadCoreCycleImage,
  deleteCoreCycleImage,
} from '../core_services';

const emptyForm = () => ({
  name: '',
  description: '',
  notes: '',
  date_range: { start: '', end: '' },
  framework: '',
});

const formFromCycle = (c) => ({
  name: c.name || '',
  description: c.description || '',
  notes: c.notes || '',
  date_range: { start: c.date_init || '', end: c.date_end || '' },
  framework: c.framework || '',
});

/**
 * Shared create/view/edit modal for a CoreCycle. Used identically by the
 * three horizon tabs (purpose/strategy/tactical) via a fixed `horizon` prop —
 * same header/body/footer shell as ModalTripForm / ModalProfessionalForm.
 *
 * date_init/date_end are ordering/filtering metadata only, independent from
 * `name` (see core module design notes, 2026-08-19) — hidden entirely for
 * horizon="purpose", shown (not required) for strategy/tactical.
 *
 * framework (optional) and the cycle's own image coexist in the schema; both
 * are shown here side by side rather than as an exclusive toggle — revisit
 * if that turns out confusing in practice.
 */
const ModalCoreCycleForm = ({ cycle, horizon, frameworks = [], onClose, onSuccess, onDelete }) => {
  const isCreate = cycle === null;
  const [isEditMode, setIsEditMode] = useState(isCreate);
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [pendingImageFile, setPendingImageFile] = useState(null);

  const showDates = horizon !== 'purpose';

  useEffect(() => {
    setShowDeleteConfirm(false);
    setPendingImageFile(null);
    if (isCreate) {
      setIsEditMode(true);
      setFormData(emptyForm());
      setImagePreview(null);
    } else {
      setIsEditMode(false);
      setFormData(formFromCycle(cycle));
      setImagePreview(cycle.image_url || null);
    }
  }, [cycle]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateRangeChange = (e) => {
    setFormData((prev) => ({ ...prev, date_range: e.target.value }));
  };

  const handleEditToggle = () => {
    setFormData(formFromCycle(cycle));
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    if (isCreate) {
      onClose();
    } else {
      setFormData(formFromCycle(cycle));
      setIsEditMode(false);
    }
  };

  const buildPayload = () => ({
    horizon,
    name: formData.name,
    description: formData.description,
    notes: formData.notes,
    date_init: showDates ? formData.date_range?.start || null : null,
    date_end: showDates ? formData.date_range?.end || null : null,
    framework: formData.framework || null,
  });

  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!okTypes.includes(file.type)) {
      toast.error('Use JPEG, PNG or WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be at most 5 MB');
      return;
    }
    setPendingImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = async () => {
    if (isCreate || !cycle?.id) {
      setPendingImageFile(null);
      setImagePreview(null);
      return;
    }
    if (!cycle.image_url && !cycle.cycle_image_object_path) {
      setPendingImageFile(null);
      setImagePreview(null);
      return;
    }
    setIsSubmitting(true);
    try {
      const updated = await deleteCoreCycleImage(cycle.id);
      setPendingImageFile(null);
      setImagePreview(updated.image_url || null);
      toast.success('Image removed');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove image');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = buildPayload();
      let cycleId = cycle?.id;
      if (isCreate) {
        const response = await createCoreCycle(payload);
        cycleId = response.data?.id;
        toast.success('Created successfully!');
      } else {
        await updateCoreCycle(cycle.id, payload);
        toast.success('Updated successfully!');
      }
      if (pendingImageFile && cycleId) {
        await uploadCoreCycleImage(cycleId, pendingImageFile);
        toast.success('Image uploaded');
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || error.response?.data?.message || 'Failed to save'
      );
      console.error('Failed to save core cycle:', error.response?.data || error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    setIsSubmitting(true);
    try {
      await onDelete(cycle.id);
      toast.success('Deleted successfully!');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to delete');
      console.error('Failed to delete core cycle:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const headerTitle = isCreate ? 'Add' : isEditMode ? 'Edit' : 'Details';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col z-10 mx-2 text-sm">

        {/* Header — compact (matches ModalTripForm / ModalProfessionalForm) */}
        <div className="flex items-center justify-between px-2 py-1 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-800 pr-2 truncate leading-none">{headerTitle}</h2>
          <div className="flex items-center gap-0 flex-shrink-0">
            {!isCreate && !isEditMode && (
              <button type="button" onClick={handleEditToggle} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg" title="Edit">
                <PencilSquareIcon className="h-5 w-5" />
              </button>
            )}
            <button type="button" onClick={onClose} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg" title="Close">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        {!isEditMode && !isCreate ? (
          <div className="flex-1 overflow-y-auto p-3 text-xs space-y-3">
            {cycle.image_url && (
              <img src={cycle.image_url} alt="" className="w-28 h-28 object-cover rounded-lg border border-gray-200" />
            )}
            <div>
              <span className="block font-medium text-gray-600">Name</span>
              <p className="text-gray-900 font-medium mt-0.5">{cycle.name}</p>
            </div>
            {cycle.description && (
              <div>
                <span className="block font-medium text-gray-600">Description</span>
                <p className="text-gray-800 whitespace-pre-line mt-0.5">{cycle.description}</p>
              </div>
            )}
            {showDates && (cycle.date_init || cycle.date_end) && (
              <div>
                <span className="block font-medium text-gray-600 mb-1">Period</span>
                <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-gray-100 w-full text-xs">
                  <CalendarIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span className="text-gray-800">
                    {cycle.date_init ? formatDate(cycle.date_init) : '—'} to{' '}
                    {cycle.date_end ? formatDate(cycle.date_end) : '—'}
                  </span>
                </div>
              </div>
            )}
            {cycle.notes && (
              <div>
                <span className="block font-medium text-gray-600">Notes</span>
                <p className="text-gray-800 whitespace-pre-line mt-0.5">{cycle.notes}</p>
              </div>
            )}
            {cycle.framework && (
              <div>
                <span className="block font-medium text-gray-600">Framework</span>
                <p className="text-gray-900 mt-0.5">
                  {frameworks.find((f) => f.id === cycle.framework)?.name || `#${cycle.framework}`}
                </p>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden min-h-0">
            <div className="flex-1 overflow-y-auto p-3 space-y-3 text-xs">
              <div>
                <label className="block font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  required
                />
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              {showDates && (
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Period (for ordering/filtering)</label>
                  <DatePicker
                    name="date_range"
                    value={formData.date_range}
                    onChange={handleDateRangeChange}
                    mode="range"
                    colorScheme="gray"
                    placeholder="Select period"
                    className="rounded-lg w-full bg-gray-100 hover:bg-gray-200 border-0"
                  />
                </div>
              )}
              <div>
                <label className="block font-medium text-gray-700 mb-1">Image</label>
                <div className="flex items-start gap-3">
                  <div className="w-24 h-24 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center text-gray-400 text-[10px]">
                    {imagePreview ? (
                      <img src={imagePreview} alt="" className="w-full h-full object-cover" />
                    ) : (
                      'No image'
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageFileChange}
                      className="text-xs max-w-[220px]"
                    />
                    <p className="text-[10px] text-gray-500">JPEG, PNG or WebP · max 5 MB</p>
                    {(imagePreview || pendingImageFile) && (
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="text-xs text-red-600 hover:underline text-left"
                      >
                        Remove image
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {frameworks.length > 0 && (
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Framework (optional)</label>
                  <select
                    name="framework"
                    value={formData.framework}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    <option value="">None</option>
                    {frameworks.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
            </div>

            <div className="flex justify-between items-center gap-2 px-3 py-2 border-t border-gray-200 flex-shrink-0 bg-white">
              {!isCreate && (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                  disabled={isSubmitting}
                >
                  Delete
                </button>
              )}
              <div className="flex gap-2 ml-auto">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </form>
        )}

        {showDeleteConfirm && !isSubmitting && (
          <ConfirmModal
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={handleDelete}
            title="Delete"
            message={`Are you sure you want to delete "${cycle?.name || formData.name}"?`}
            confirmText="Delete"
            cancelText="Cancel"
            overlayClassName="z-[70]"
          />
        )}
      </div>
    </div>
  );
};

export default ModalCoreCycleForm;
